import { customElement, query, state } from 'lit/decorators.js';
import { LitElement, html, unsafeCSS } from 'lit';
import { styles as componentStyles } from '../view/toolbarStyles.scss';
import { translateFn } from '../../../utils/translator.helper';
import { GzIconWebComponent } from '../../gzIcon';
import { GzIconButtonWebComponent } from '../../gzIconButton';
import { getLinkAndImageTypes, getListTypes, getMarkdownHeadingLevel, isBold, isItalic } from './helpers';
import { ButtonColor } from '../../gzIconButton/types';
export enum MDEditorToolbarActions {
  BOLD = 'bold',
  ITALIC = 'italic',
  ORDERED_LIST = 'ordered-list',
  BULLET_LIST = 'bullet-list',
  LINK = 'link',
  RESET = 'reset',
  PREVIEW = 'preview',
  COPY_TO_CLIPBOARD = 'copy-to-clipboard',
}

export interface MDEditorTextSizeOption {
  title: string;
  value: string;
}

/**
 * @summary This component is used within the gz-markdown-editor component for displaying the format (i.e. bold, italic, list etc) and action buttons (copy content, reset etc).
 * @tag gz-md-editor-toolbar
 * @fires text-size-change - Custom event dispatched when the selected text size is changed from the respective selector.
 * @fires toolbar-button-click - Custom event dispatched when any of the format or action button is clicked.
 */
@customElement('gz-md-editor-toolbar')
export class GzMarkdownEditorToolbarWebComponent extends LitElement {
  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  private textSizeOptions: MDEditorTextSizeOption[] = [
    {
      title: translateFn('Normal text'),
      value: '0',
    },
    {
      title: `[#] ${translateFn('Heading 1')}`,
      value: '1',
    },
    {
      title: `[##] ${translateFn('Heading 2')}`,
      value: '2',
    },
    {
      title: `[###] ${translateFn('Heading 3')}`,
      value: '3',
    },
    {
      title: `[####] ${translateFn('Heading 4')}`,
      value: '4',
    },
  ];

  @query('#boldIcon')
  private boldIcon: GzIconWebComponent;

  @query('#italicIcon')
  private italicIcon: GzIconWebComponent;

  @query('#olIcon')
  private olIcon: GzIconWebComponent;

  @query('#ulIcon')
  private ulIcon: GzIconWebComponent;

  @query('#linkIcon')
  private linkIcon: GzIconButtonWebComponent;

  @query('#resetContentButton')
  private resetContentButton: GzIconButtonWebComponent;

  @state()
  private selectedTextSize: string | undefined = this.textSizeOptions[0].title;

  render() {
    return html`
      <div class="toolbar">
        <div class="format-controls">
          <gz-md-editor-font-size-selector
            .selectedTextSize="${this.selectedTextSize}"
            .textSizeOptions="${this.textSizeOptions}"
          ></gz-md-editor-font-size-selector>

          <gz-tooltip tooltipText="${translateFn('Bold')}">
            <button
              class="toolbar-button"
              id="boldBtn"
              tabindex="-1"
              @click="${() => this.onButtonClick(MDEditorToolbarActions.BOLD)}"
            >
              <gz-icon color="secondary" size="24" id="boldIcon">
                <svg slot="custom-svg">
                  <g>
                    <path
                      id="Vector"
                      d="M6 20.4367V4H12.3871C13.6873 4 14.8536 4.39454 15.8859 5.18362C16.9181 5.9727 17.4342 7.03226 17.4342 8.36228C17.4342 9.24566 17.2184 9.98759 16.7866 10.5881C16.3548 11.1886 15.866 11.6179 15.3201 11.8759V11.9653C15.995 12.1836 16.6104 12.6203 17.1663 13.2754C17.7221 13.9305 18 14.7792 18 15.8213C18 17.3598 17.4144 18.5136 16.2432 19.2829C15.072 20.0521 13.8561 20.4367 12.5955 20.4367H6ZM9.17122 17.5484H12.4467C13.2804 17.5484 13.8834 17.3176 14.2556 16.8561C14.6278 16.3945 14.8139 15.9355 14.8139 15.4789C14.8139 15.0223 14.6278 14.5633 14.2556 14.1017C13.8834 13.6402 13.2655 13.4094 12.402 13.4094H9.17122V17.5484ZM9.17122 10.6402H12.1638C12.8189 10.6402 13.3424 10.4541 13.7345 10.0819C14.1266 9.70968 14.3226 9.26055 14.3226 8.73449C14.3226 8.17866 14.1166 7.72457 13.7047 7.37221C13.2928 7.01985 12.7891 6.84367 12.1935 6.84367H9.17122V10.6402Z"
                    />
                  </g>
                </svg>
              </gz-icon>
            </button>
          </gz-tooltip>

          <gz-tooltip tooltipText="${translateFn('Italic')}">
            <button
              class="toolbar-button"
              id="italicBtn"
              tabindex="-1"
              @click="${() => this.onButtonClick(MDEditorToolbarActions.ITALIC)}"
            >
              <gz-icon color="secondary" size="24" id="italicIcon">
                <svg slot="custom-svg">
                  <g>
                    <path
                      id="Vector"
                      d="M5 19.035V16.4475H9.34436L12.6265 6.58755H8.24125V4H19V6.58755H15.2004L11.9183 16.4475H15.7588V19.035H5Z"
                    />
                  </g>
                </svg>
              </gz-icon>
            </button>
          </gz-tooltip>

          <span class="divider"></span>

          <gz-tooltip tooltipText="${translateFn('Bulleted list')}">
            <button
              class="toolbar-button"
              id="ulBtn"
              tabindex="-1"
              @click="${() => this.onButtonClick(MDEditorToolbarActions.BULLET_LIST)}"
            >
              <gz-icon color="secondary" size="24" id="ulIcon">
                <svg slot="custom-svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.5625 8C5.66707 8 6.5625 7.10457 6.5625 6C6.5625 4.89543 5.66707 4 4.5625 4C3.45793 4 2.5625 4.89543 2.5625 6C2.5625 7.10457 3.45793 8 4.5625 8ZM20.5625 5H8.5625V7H20.5625V5ZM8.5625 11H20.5625V13H8.5625V11ZM4.5625 14C5.66707 14 6.5625 13.1046 6.5625 12C6.5625 10.8954 5.66707 10 4.5625 10C3.45793 10 2.5625 10.8954 2.5625 12C2.5625 13.1046 3.45793 14 4.5625 14ZM8.5625 17H20.5625V19H8.5625V17ZM4.5625 20C5.66707 20 6.5625 19.1046 6.5625 18C6.5625 16.8954 5.66707 16 4.5625 16C3.45793 16 2.5625 16.8954 2.5625 18C2.5625 19.1046 3.45793 20 4.5625 20Z"
                  />
                </svg>
              </gz-icon>
            </button>
          </gz-tooltip>

          <gz-tooltip tooltipText="${translateFn('Numbered list')}">
            <button
              class="toolbar-button"
              id="olBtn"
              tabindex="-1"
              @click="${() => this.onButtonClick(MDEditorToolbarActions.ORDERED_LIST)}"
            >
              <gz-icon color="secondary" size="24" id="olIcon">
                <svg slot="custom-svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.20263 7.27401V8.00001H7.11863V7.27401H6.23063V3.81201H4.85063L3.84863 5.13201L4.42463 5.58201L5.27663 4.46601H5.33063V7.27401H4.20263ZM8.9375 5H20.9375V7H8.9375V5ZM3.9567 14H6.9687V13.244H4.9527L5.7507 12.626C5.9067 12.506 6.0527 12.386 6.1887 12.266C6.3287 12.146 6.4487 12.022 6.5487 11.894C6.6527 11.766 6.7327 11.63 6.7887 11.486C6.8487 11.338 6.8787 11.176 6.8787 11C6.8787 10.82 6.8467 10.654 6.7827 10.502C6.7187 10.346 6.6247 10.212 6.5007 10.1C6.3767 9.98799 6.2247 9.89999 6.0447 9.83599C5.8647 9.77199 5.6607 9.73999 5.4327 9.73999C5.2087 9.73999 5.0107 9.76999 4.8387 9.82999C4.6667 9.88599 4.5147 9.96199 4.3827 10.058C4.2547 10.154 4.1487 10.268 4.0647 10.4C3.9807 10.528 3.9147 10.664 3.8667 10.808L4.6527 11.108C4.6967 10.948 4.7727 10.81 4.8807 10.694C4.9887 10.578 5.1427 10.52 5.3427 10.52C5.5187 10.52 5.6587 10.566 5.7627 10.658C5.8667 10.746 5.9187 10.878 5.9187 11.054V11.114C5.9187 11.274 5.8687 11.426 5.7687 11.57C5.6687 11.714 5.5047 11.884 5.2767 12.08L3.9567 13.178V14ZM8.9375 11H20.9375V13H8.9375V11ZM5.78065 17.336C5.67665 17.424 5.50865 17.468 5.27665 17.468H4.88065V18.218H5.27065C5.50665 18.218 5.69065 18.262 5.82265 18.35C5.95465 18.434 6.02065 18.562 6.02065 18.734V18.776C6.02065 18.952 5.96265 19.09 5.84665 19.19C5.73065 19.29 5.56465 19.34 5.34865 19.34C5.11665 19.34 4.93065 19.286 4.79065 19.178C4.65465 19.066 4.54065 18.924 4.44865 18.752L3.78265 19.268C3.85065 19.372 3.92865 19.474 4.01665 19.574C4.10865 19.67 4.21665 19.756 4.34065 19.832C4.46465 19.904 4.60665 19.962 4.76665 20.006C4.93065 20.05 5.11865 20.072 5.33065 20.072C5.57865 20.072 5.80265 20.044 6.00265 19.988C6.20665 19.928 6.37865 19.844 6.51865 19.736C6.66265 19.628 6.77265 19.498 6.84865 19.346C6.92865 19.194 6.96865 19.024 6.96865 18.836C6.96865 18.548 6.88265 18.32 6.71065 18.152C6.54265 17.98 6.33065 17.872 6.07465 17.828V17.792C6.18665 17.772 6.29065 17.738 6.38665 17.69C6.48665 17.638 6.57265 17.572 6.64465 17.492C6.72065 17.412 6.78065 17.318 6.82465 17.21C6.86865 17.102 6.89065 16.982 6.89065 16.85C6.89065 16.682 6.85465 16.53 6.78265 16.394C6.71065 16.258 6.60865 16.142 6.47665 16.046C6.34865 15.946 6.19265 15.87 6.00865 15.818C5.82465 15.766 5.61865 15.74 5.39065 15.74C5.20665 15.74 5.04065 15.758 4.89265 15.794C4.74865 15.826 4.61865 15.874 4.50265 15.938C4.38665 15.998 4.28065 16.072 4.18465 16.16C4.08865 16.244 4.00065 16.338 3.92065 16.442L4.49065 16.97C4.59465 16.81 4.72065 16.688 4.86865 16.604C5.02065 16.516 5.18265 16.472 5.35465 16.472C5.54265 16.472 5.68665 16.518 5.78665 16.61C5.89065 16.698 5.94265 16.818 5.94265 16.97V17.012C5.94265 17.14 5.88865 17.248 5.78065 17.336ZM20.9375 17H8.9375V19H20.9375V17Z"
                  />
                </svg>
              </gz-icon>
            </button>
          </gz-tooltip>

          <span class="divider"></span>

          <gz-tooltip tooltipText="${translateFn('Add link')}">
            <gz-icon-button
              id="linkIcon"
              size="32"
              fontSize="24"
              icon="ds-icon-resource_URL"
              buttonType="square"
              buttonFill="quiet"
              buttonColor="secondary"
              tabindex="-1"
              @click="${() => this.onButtonClick(MDEditorToolbarActions.LINK)}"
            ></gz-icon-button>
          </gz-tooltip>
        </div>

        <div class="actions">
          <gz-icon-button
            icon="ds-icon-refresh"
            id="resetContentButton"
            disabled
            size="32"
            fontSize="24"
            buttonType="square"
            buttonFill="quiet"
            buttonColor="secondary"
            tabindex="-1"
            @click="${() => this.onButtonClick(MDEditorToolbarActions.RESET)}"
            tooltipText="${translateFn('Reset to default')}"
          >
          </gz-icon-button>
          <gz-icon-button
            icon="ds-icon-copy_clipboard"
            id="copyContentButton"
            size="32"
            fontSize="22"
            buttonType="square"
            buttonFill="quiet"
            buttonColor="secondary"
            tabindex="-1"
            @click="${e => this.onButtonClick(MDEditorToolbarActions.COPY_TO_CLIPBOARD, e)}"
            tooltipText="${translateFn('Copy to clipboard')}"
          >
          </gz-icon-button>

          <gz-tooltip tooltipText="${translateFn('Preview')}">
            <button
              class="toolbar-button"
              id="previewBtn"
              tabindex="-1"
              @click="${() => this.onButtonClick(MDEditorToolbarActions.PREVIEW)}"
            >
              <gz-icon color="secondary" size="24" id="previewIcon">
                <svg fill="var(--chart-300-icon-buttons)" viewBox="0 0 24 24" slot="custom-svg" width="24" height="24">
                  <g>
                    <path
                      d="M20.553 3H14.523C14.4042 3 14.2902 3.04766 14.2062 3.1325C14.1222 3.21733 14.075 3.3324 14.075 3.45237V4.31269C14.075 4.43267 14.1222 4.54773 14.2062 4.63257C14.2902 4.7174 14.4042 4.76506 14.523 4.76506H18.023L13.05 9.79064C13.0078 9.83163 12.9743 9.88079 12.9513 9.93518C12.9284 9.98958 12.9166 10.0481 12.9166 10.1072C12.9166 10.1663 12.9284 10.2248 12.9513 10.2792C12.9743 10.3336 13.0078 10.3828 13.05 10.4238L13.65 11.0296C13.6903 11.0705 13.7381 11.1029 13.7908 11.1251C13.8434 11.1473 13.8999 11.1588 13.957 11.1589H13.964C14.0814 11.1591 14.1942 11.1127 14.278 11.0296L19.259 6V9.53416C19.259 9.65414 19.3062 9.7692 19.3902 9.85404C19.4743 9.93888 19.5882 9.98654 19.707 9.98654H20.552C20.611 9.98654 20.6694 9.97477 20.7239 9.95191C20.7784 9.92905 20.8279 9.89555 20.8695 9.85333C20.9111 9.81111 20.9441 9.761 20.9665 9.70588C20.9889 9.65076 21.0003 9.59172 21 9.53214V3.4554C21.0004 3.39574 20.9891 3.33659 20.9668 3.28136C20.9444 3.22612 20.9115 3.17589 20.8699 3.13357C20.8282 3.09124 20.7787 3.05765 20.7242 3.03472C20.6696 3.0118 20.6121 3 20.553 3ZM5.97002 4.76203H9.47002C9.58762 4.76098 9.7001 4.71326 9.78317 4.62919C9.86624 4.54513 9.91323 4.43144 9.91402 4.31269V4.29653V3.45641C9.91455 3.39667 9.90335 3.33741 9.88107 3.28206C9.85879 3.2267 9.82588 3.17636 9.78422 3.13392C9.74257 3.09149 9.69301 3.0578 9.63839 3.03482C9.58377 3.01183 9.52519 3 9.46602 3H3.44802C3.38885 3 3.33026 3.01183 3.27565 3.03482C3.22103 3.0578 3.17146 3.09149 3.12981 3.13392C3.08816 3.17636 3.05524 3.2267 3.03296 3.28206C3.01069 3.33741 2.99949 3.39667 3.00002 3.45641V9.53315C3.00002 9.65313 3.04722 9.76819 3.13123 9.85303C3.21525 9.93787 3.3292 9.98553 3.44802 9.98553H4.28002C4.39883 9.98553 4.51279 9.93787 4.5968 9.85303C4.68082 9.76819 4.72802 9.65313 4.72802 9.53315V5.99899L9.72102 11.0326C9.80481 11.1157 9.9176 11.1621 10.035 11.1619H10.041C10.0983 11.1619 10.1549 11.1505 10.2078 11.1283C10.2607 11.1062 10.3087 11.0736 10.349 11.0326L10.949 10.4268C10.9912 10.3858 11.0248 10.3366 11.0477 10.2823C11.0706 10.2279 11.0825 10.1693 11.0825 10.1102C11.0825 10.0511 11.0706 9.99261 11.0477 9.93821C11.0248 9.88382 10.9912 9.83466 10.949 9.79367L5.97002 4.76203ZM20.553 14.0316H19.7C19.5846 14.0316 19.474 14.0779 19.3924 14.1603C19.3108 14.2427 19.265 14.3544 19.265 14.4709V18.005L14.28 12.9714C14.2394 12.9288 14.1907 12.8949 14.1369 12.8717C14.083 12.8486 14.0251 12.8367 13.9665 12.8367C13.908 12.8367 13.85 12.8486 13.7962 12.8717C13.7423 12.8949 13.6936 12.9288 13.653 12.9714L13.053 13.5772C13.0108 13.6182 12.9773 13.6674 12.9543 13.7218C12.9314 13.7762 12.9196 13.8347 12.9196 13.8938C12.9196 13.9529 12.9314 14.0114 12.9543 14.0658C12.9773 14.1202 13.0108 14.1694 13.053 14.2104L18.034 19.24H14.523C14.4042 19.24 14.2902 19.2876 14.2062 19.3725C14.1222 19.4573 14.075 19.5724 14.075 19.6924V20.5456C14.075 20.6656 14.1222 20.7806 14.2062 20.8655C14.2902 20.9503 14.4042 20.998 14.523 20.998H20.54C20.6588 20.998 20.7728 20.9503 20.8568 20.8655C20.9408 20.7806 20.988 20.6656 20.988 20.5456V14.4699C20.9846 14.3522 20.9359 14.2405 20.8523 14.1585C20.7686 14.0764 20.6566 14.0306 20.54 14.0306L20.553 14.0316ZM10.353 12.9714C10.3124 12.9288 10.2637 12.8949 10.2099 12.8717C10.156 12.8486 10.0981 12.8367 10.0395 12.8367C9.98097 12.8367 9.92303 12.8486 9.86916 12.8717C9.81529 12.8949 9.7666 12.9288 9.72602 12.9714L4.74202 18V14.4658C4.74202 14.3493 4.69619 14.2376 4.61461 14.1552C4.53303 14.0729 4.42239 14.0266 4.30702 14.0266H3.44802C3.33057 14.0266 3.21782 14.0731 3.13403 14.1562C3.05024 14.2393 3.00212 14.3523 3.00002 14.4709V20.5476C3.00002 20.6676 3.04722 20.7827 3.13123 20.8675C3.21525 20.9523 3.3292 21 3.44802 21H9.46602C9.58483 21 9.69879 20.9523 9.7828 20.8675C9.86682 20.7827 9.91402 20.6676 9.91402 20.5476V19.6944C9.91402 19.5744 9.86682 19.4593 9.7828 19.3745C9.69879 19.2897 9.58483 19.242 9.46602 19.242H5.96602L10.952 14.2083C10.9942 14.1674 11.0278 14.1182 11.0507 14.0638C11.0736 14.0094 11.0855 13.9509 11.0855 13.8918C11.0855 13.8327 11.0736 13.7742 11.0507 13.7198C11.0278 13.6654 10.9942 13.6162 10.952 13.5752L10.353 12.9714Z"
                    ></path>
                  </g>
                </svg>
              </gz-icon>
            </button>
          </gz-tooltip>
        </div>
      </div>
    `;
  }

  private onButtonClick(action: string, event?: MouseEvent): void {
    this.dispatchEvent(
      new CustomEvent('toolbar-button-click', {
        detail: {
          action,
          event,
        },
      })
    );
  }

  public updateFormatButtonsStates(selectedText: string, asteriskCount: number): void {
    this.boldIcon.color = isBold(asteriskCount) ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY;
    this.italicIcon.color = isItalic(asteriskCount) ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY;

    const { isBulletList, isOrderedList } = getListTypes(selectedText);

    this.olIcon.color = isOrderedList ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY;
    this.ulIcon.color = isBulletList ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY;

    const { isLink } = getLinkAndImageTypes(selectedText);

    this.linkIcon.buttonColor = isLink ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY;

    if (selectedText === '') {
      this.selectedTextSize = this.textSizeOptions[0].title;
    } else {
      const headingLevel = getMarkdownHeadingLevel(selectedText);
      const selectedValue = this.textSizeOptions.find(option => option.value === headingLevel.toString());
      this.selectedTextSize = selectedValue?.title;
    }
  }

  public updateResetButtonState(isDirty: boolean): void {
    this.resetContentButton.disabled = !isDirty;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-md-editor-toolbar': GzMarkdownEditorToolbarWebComponent;
  }
}
